import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Trash2,
  ShieldAlert,
  RefreshCw,
  Users,
  Database,
  CheckCircle2,
  AlertTriangle,
  Eraser,
  Download,
  History,
  Calendar,
  UserX,
} from 'lucide-react';
import backendApi from '../../services/backendApi';
import { useAuth } from '../../context/AuthContext';


interface CleanupUser {
  user_id: number;
  username: string;
  email?: string;
  name?: string;
  role?: string;
  company_id?: number | null;
  company_name?: string | null;
  site_id?: number | null;
  site_name?: string | null;
  site_code?: string | null;
}

interface SummarySheet {
  key: string;
  label: string;
  count: number;
}

interface SummaryModule {
  key: string;
  label: string;
  total_count: number;
  sheets: SummarySheet[];
}

interface CleanupSummary {
  user_id: number;
  company_id?: number | null;
  site_id?: number | null;
  grand_total: number;
  modules: SummaryModule[];
}

const DataCleanupManagement: React.FC = () => {
  const { user: currentUser } = useAuth();

  const roleName = String((currentUser as any)?.roleName || currentUser?.role || '').toLowerCase();
  const isSystemAdmin =
    !!currentUser?.isGlobalAdmin ||
    !!(currentUser as any)?.is_superuser ||
    !!(currentUser as any)?.is_super_admin ||
    ['system_admin', 'super_admin'].includes(roleName);

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [executing, setExecuting] = useState(false);

  const [users, setUsers] = useState<CleanupUser[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);

  const [companies, setCompanies] = useState<any[]>([]);
  const [sites, setSites] = useState<any[]>([]);

  const [summary, setSummary] = useState<CleanupSummary | null>(null);
  const [selectedModules, setSelectedModules] = useState<Record<string, boolean>>({});
  const [selectedSheets, setSelectedSheets] = useState<Record<string, Record<string, boolean>>>({});
  const [resultRows, setResultRows] = useState<any[]>([]);
  const [backupHistory, setBackupHistory] = useState<any[]>([]);

  const fetchHistory = async () => {
    const res = await backendApi.getCleanupBackupHistory();
    if (res.success) {
      setBackupHistory(res.data);
    }
  };

  useEffect(() => {
    if (isSystemAdmin) {
      fetchHistory();
    }
  }, [isSystemAdmin]);

  const selectedUser = useMemo(
    () => users.find((u) => u.user_id === selectedUserId) || null,
    [users, selectedUserId]
  );

  const fetchUsers = async (searchText = userSearch, compId = selectedCompanyId, sId = selectedSiteId) => {
    setLoadingUsers(true);
    const res = await backendApi.getCleanupUsers({ search: searchText, company_id: compId, site_id: sId });
    if (res.success) {
      setUsers(res.users || []);
    }
    setLoadingUsers(false);
  };

  const fetchSummary = async (moduleKeys?: string[]) => {
    if (!selectedUserId && !selectedCompanyId && !selectedSiteId) {
      setSummary(null);
      return;
    }
    setLoadingSummary(true);
    const res = await backendApi.getCleanupSummary({
      user_id: selectedUserId,
      company_id: selectedCompanyId,
      site_id: selectedSiteId,
      module_keys: moduleKeys,
    });
    if (res.success && res.summary) {
      const nextSummary = res.summary as CleanupSummary;
      setSummary(nextSummary);
      setSelectedModules((prev) => {
        const next: Record<string, boolean> = {};
        (nextSummary.modules || []).forEach((m) => {
          next[m.key] = prev[m.key] ?? false;
        });
        return next;
      });
      setSelectedSheets((prev) => {
        const next: Record<string, Record<string, boolean>> = {};
        (nextSummary.modules || []).forEach((m) => {
          const moduleSheets = prev[m.key] || {};
          next[m.key] = {};
          (m.sheets || []).forEach((s) => {
            next[m.key][s.key] = moduleSheets[s.key] ?? false;
          });
        });
        return next;
      });
    }
    setLoadingSummary(false);
  };

  useEffect(() => {
    const fetchCompaniesAndSites = async () => {
      const [companiesRes, sitesRes] = await Promise.all([
        backendApi.getCompanies(),
        backendApi.getSites(false)
      ]);
      if (companiesRes.success) {
        setCompanies(companiesRes.companies || []);
      }
      if (sitesRes.success) {
        setSites(sitesRes.sites || []);
      }
    };
    if (isSystemAdmin) {
      void fetchCompaniesAndSites();
      void fetchUsers();
    }
  }, [isSystemAdmin]);

  useEffect(() => {
    if (isSystemAdmin) {
      void fetchUsers(userSearch, selectedCompanyId, selectedSiteId);
    }
  }, [selectedCompanyId, selectedSiteId]);

  useEffect(() => {
    if (selectedUserId || selectedCompanyId || selectedSiteId) {
      void fetchSummary();
    } else {
      setSummary(null);
    }
  }, [selectedUserId, selectedCompanyId, selectedSiteId]);

  const selectedModuleKeys = useMemo(
    () => Object.keys(selectedModules).filter((k) => selectedModules[k]),
    [selectedModules]
  );

  const selectedSheetKeysByModule = useMemo(() => {
    const result: Record<string, string[]> = {};
    selectedModuleKeys.forEach((moduleKey) => {
      const sheets = selectedSheets[moduleKey] || {};
      const chosen = Object.keys(sheets).filter((sheetKey) => sheets[sheetKey]);
      if (chosen.length > 0) {
        result[moduleKey] = chosen;
      }
    });
    return result;
  }, [selectedModuleKeys, selectedSheets]);

  const totalSelectedSheets = useMemo(
    () => Object.values(selectedSheetKeysByModule).reduce((acc, arr) => acc + arr.length, 0),
    [selectedSheetKeysByModule]
  );

  const handleModuleToggle = (module: SummaryModule, checked: boolean) => {
    setSelectedModules((prev) => ({ ...prev, [module.key]: checked }));
    setSelectedSheets((prev) => {
      const nextModuleSheets: Record<string, boolean> = {};
      (module.sheets || []).forEach((sheet) => {
        nextModuleSheets[sheet.key] = checked;
      });
      return {
        ...prev,
        [module.key]: nextModuleSheets,
      };
    });
  };

  const handleSheetToggle = (module: SummaryModule, sheet: SummarySheet, checked: boolean) => {
    setSelectedSheets((prev) => {
      const existing = prev[module.key] || {};
      const updatedModuleSheets = {
        ...existing,
        [sheet.key]: checked,
      };

      const hasAnySelected = Object.values(updatedModuleSheets).some(Boolean);
      setSelectedModules((prevModules) => ({
        ...prevModules,
        [module.key]: hasAnySelected,
      }));

      return {
        ...prev,
        [module.key]: updatedModuleSheets,
      };
    });
  };

  const handleRunCleanup = async (runMode: 'backup' | 'delete') => {
    if ((!selectedUserId && !selectedCompanyId && !selectedSiteId) || selectedModuleKeys.length === 0) {
      alert('Please select a target (Company/Site/User) and at least one module.');
      return;
    }

    if (totalSelectedSheets === 0) {
      alert('Please select at least one sheet to continue.');
      return;
    }

    const isBackupOnly = runMode === 'backup';
    const actionText = isBackupOnly ? 'backup only' : 'permanent delete (backup will be taken first)';
    if (!window.confirm(`Confirm ${actionText} for selected modules?`)) {
      return;
    }

    setExecuting(true);
    const res = await backendApi.executeCleanup({
      user_id: selectedUserId,
      company_id: selectedCompanyId,
      site_id: selectedSiteId,
      module_keys: selectedModuleKeys,
      sheet_keys: selectedSheetKeysByModule,
      dry_run: isBackupOnly,
    });

    if (res.success) {
      setResultRows(res?.result?.results || []);
      await fetchSummary(selectedModuleKeys);
      alert(res.message || 'Operation completed.');
    } else {
      alert(res.error || 'Cleanup operation failed.');
    }
    setExecuting(false);
  };

  const handleDeleteBackup = async (backupPath: string) => {
    if (!window.confirm("Are you sure you want to permanently delete this backup file from the server?")) {
      return;
    }
    const res = await backendApi.deleteCleanupBackup(backupPath);
    if (res.success) {
      alert(res.message || 'Backup deleted successfully.');
      setResultRows(prev => prev.map(row => row.backup_file_path === backupPath ? { ...row, backup_file_path: null } : row));
      fetchHistory();
    } else {
      alert(res.error || 'Failed to delete backup.');
    }
  };

  if (!isSystemAdmin) {
    return (
      <div className="min-h-screen pt-20 lg:pt-4 pb-8 px-4">
        <div className="max-w-2xl mx-auto mt-20 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldAlert size={28} className="text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-800">Access Denied</h2>
          <p className="text-gray-500 mt-2">Only System Admin can access data cleanup controls.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 lg:pt-4 pb-10 px-2 sm:px-4">
      <div className="max-w-7xl mx-auto space-y-5">
        <motion.div initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} className="admin-hero p-5">
          <div className="admin-toolbar">
            <div className="space-y-1">
              <p className="admin-eyebrow">System cleanup controls</p>
              <div className="flex items-center gap-3">
                <div className="hidden sm:flex w-11 h-11 rounded-[14px] bg-gradient-to-br from-[#dc2626] to-[#7f1d1d] items-center justify-center text-white shadow-lg">
                  <Eraser size={22} />
                </div>
                <div>
                  <h1 className="admin-page-title">User Data Cleanup</h1>
                  <p className="admin-page-subtitle">Take backup and selectively clean uploaded data module-wise.</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/admin/data-deletion-requests"
                className="admin-button admin-button-secondary inline-flex items-center gap-2"
              >
                <UserX size={16} />
                Deletion Requests
              </Link>
              <button
                type="button"
                onClick={() => fetchUsers(userSearch, selectedCompanyId, selectedSiteId)}
                className="admin-button admin-button-primary"
                disabled={loadingUsers}
              >
                <RefreshCw size={18} className={`mr-2 ${loadingUsers ? 'animate-spin' : ''}`} />
                Refresh Users
              </button>
            </div>
          </div>
        </motion.div>


        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-1 admin-panel p-4">
            <h3 className="admin-panel-title flex items-center gap-2">
              <Database size={18} />
              Select Target Scope
            </h3>
            
            <div className="mt-4 space-y-4">
              <div>
                <label className="font-medium text-slate-700 text-sm mb-1 block">Company</label>
                <select
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={selectedCompanyId || ''}
                  onChange={(e) => {
                    setSelectedCompanyId(e.target.value ? Number(e.target.value) : null);
                    setSelectedSiteId(null);
                    setSelectedUserId(null);
                  }}
                >
                  <option value="">All Companies (Global)</option>
                  {companies.map((c) => (
                    <option key={c.company_id} value={c.company_id}>{c.company_name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-medium text-slate-700 text-sm mb-1 block">Site</label>
                <select
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={selectedSiteId || ''}
                  onChange={(e) => {
                    setSelectedSiteId(e.target.value ? Number(e.target.value) : null);
                    setSelectedUserId(null);
                  }}
                  disabled={!!selectedCompanyId && sites.filter(s => s.company_id === selectedCompanyId).length === 0}
                >
                  <option value="">All Sites</option>
                  {sites
                    .filter(s => (selectedCompanyId ? s.company_id === selectedCompanyId : true))
                    .map((s) => (
                      <option key={s.site_id} value={s.site_id}>{s.site_name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-medium text-slate-700 text-sm mb-1 block flex items-center gap-2">
                  <Users size={14} /> User (optional)
                </label>
                <input
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  onBlur={() => fetchUsers(userSearch, selectedCompanyId, selectedSiteId)}
                  placeholder="Search username/email"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm mb-2"
                />
                <select
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={selectedUserId || ''}
                  onChange={(e) => setSelectedUserId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">Select user</option>
                  {users.map((u) => (
                    <option key={u.user_id} value={u.user_id}>
                      {u.username} ({u.role || 'user'})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selectedUser && (
              <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700 space-y-1">
                <p><span className="font-semibold">Name:</span> {selectedUser.name || selectedUser.username}</p>
                <p><span className="font-semibold">Email:</span> {selectedUser.email || '-'}</p>
                <p><span className="font-semibold">Company:</span> {selectedUser.company_name || '-'}</p>
                <p><span className="font-semibold">Site:</span> {selectedUser.site_name || '-'}</p>
              </div>
            )}
          </div>

          <div className="xl:col-span-2 admin-panel p-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="admin-panel-title flex items-center gap-2">
                <Database size={18} />
                Module Summary
              </h3>
              <div className="inline-flex items-center gap-2 text-sm text-slate-600">
                <span>Total Rows:</span>
                <span className="font-bold text-slate-900">{summary?.grand_total || 0}</span>
              </div>
            </div>

            {loadingSummary ? (
              <div className="py-10 text-center text-slate-500">Loading summary...</div>
            ) : (
              <div className="space-y-3 mt-3">
                {(summary?.modules || []).map((module) => (
                  <div key={module.key} className="rounded-2xl border border-slate-200 overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50 flex items-center justify-between">
                      <label className="inline-flex items-center gap-3 font-semibold text-slate-800">
                        <input
                          type="checkbox"
                          checked={!!selectedModules[module.key]}
                          onChange={(e) => handleModuleToggle(module, e.target.checked)}
                        />
                        {module.label}
                      </label>
                      <span className="text-sm font-semibold text-slate-700">{module.total_count} rows</span>
                    </div>
                    <div className="px-4 py-3 grid sm:grid-cols-2 gap-2 text-sm">
                      {module.sheets.map((sheet) => (
                        <div key={sheet.key} className="rounded-xl border border-slate-200 px-3 py-2 flex justify-between">
                          <label className="inline-flex items-center gap-2 text-slate-700">
                            <input
                              type="checkbox"
                              checked={!!selectedSheets[module.key]?.[sheet.key]}
                              onChange={(e) => handleSheetToggle(module, sheet, e.target.checked)}
                            />
                            <span>{sheet.label}</span>
                          </label>
                          <span className="font-semibold text-slate-900">{sheet.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => handleRunCleanup('backup')}
                disabled={executing || (!selectedUserId && !selectedCompanyId && !selectedSiteId) || selectedModuleKeys.length === 0 || totalSelectedSheets === 0}
                className="inline-flex items-center gap-2 rounded-xl bg-[#0066A3] text-white px-4 py-2 text-sm font-semibold shadow-sm hover:bg-[#1C4D88] disabled:opacity-60"
              >
                {executing ? <RefreshCw size={16} className="animate-spin" /> : <Database size={16} />}
                Backup Only
              </button>
              <button
                type="button"
                onClick={() => handleRunCleanup('delete')}
                disabled={executing || (!selectedUserId && !selectedCompanyId && !selectedSiteId) || selectedModuleKeys.length === 0 || totalSelectedSheets === 0}
                className="inline-flex items-center gap-2 rounded-xl bg-red-600 text-white px-4 py-2 text-sm font-semibold shadow-sm hover:bg-red-700 disabled:opacity-60"
              >
                {executing ? <RefreshCw size={16} className="animate-spin" /> : <Trash2 size={16} />}
                Permanent Delete
              </button>
              <button
                type="button"
                onClick={() => fetchSummary(selectedModuleKeys)}
                disabled={!selectedUserId && !selectedCompanyId && !selectedSiteId}
                className="admin-btn-secondary inline-flex items-center gap-2"
              >
                <RefreshCw size={16} />
                Refresh Summary
              </button>
              <div className="inline-flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                <AlertTriangle size={14} />
                Selected sheets: {totalSelectedSheets}. Permanent Delete action is irreversible.
              </div>
            </div>
          </div>
        </div>

        {resultRows.length > 0 && (
          <div className="admin-panel p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="admin-panel-title">Execution Result</h3>
              {resultRows[0]?.backup_file_path && (
                <div className="flex items-center gap-2">
                  <a 
                    href={`${(process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api').replace('/api', '')}/media/${resultRows[0].backup_file_path}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="admin-btn-primary flex items-center gap-2 text-sm px-3 py-1.5"
                  >
                    <Download size={16} /> Download Backup ZIP
                  </a>
                  <button
                    onClick={() => handleDeleteBackup(resultRows[0].backup_file_path)}
                    className="admin-btn-secondary !bg-red-50 !text-red-600 !border-red-200 hover:!bg-red-100 flex items-center gap-2 text-sm px-3 py-1.5"
                  >
                    <Trash2 size={16} /> Delete Backup
                  </button>
                </div>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-slate-200">
                    <th className="py-2 pr-2">Module</th>
                    <th className="py-2 pr-2">Sheet</th>
                    <th className="py-2 pr-2">Rows</th>
                    <th className="py-2 pr-2">Deleted</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {resultRows.map((row, idx) => (
                    <tr key={`${row.audit_id || idx}`} className="border-b border-slate-100">
                      <td className="py-2 pr-2">{row.module_key}</td>
                      <td className="py-2 pr-2">{row.sheet_key}</td>
                      <td className="py-2 pr-2">{row.row_count}</td>
                      <td className="py-2 pr-2">{row.deleted_count}</td>
                      <td className="py-2 pr-2">
                        <span className="inline-flex items-center gap-1">
                          {row.status === 'success' ? <CheckCircle2 size={14} className="text-emerald-600" /> : <AlertTriangle size={14} className="text-amber-600" />}
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {backupHistory.length > 0 && (
          <div className="admin-panel p-4 mt-6">
            <div className="flex items-center gap-2 mb-4">
              <History className="text-gray-500" size={18} />
              <h3 className="admin-panel-title !mb-0">Past Backups</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-slate-200">
                    <th className="py-2 pr-2">Date</th>
                    <th className="py-2 pr-2">Scope</th>
                    <th className="py-2 pr-2">Modules</th>
                    <th className="py-2 pr-2">Rows</th>
                    <th className="py-2 pr-2">Requested By</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {backupHistory.map((backup, idx) => (
                    <tr key={idx} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                      <td className="py-2 pr-2 whitespace-nowrap">
                        <span className="flex items-center gap-1 text-gray-600">
                          <Calendar size={14} />
                          {new Date(backup.created_at).toLocaleString()}
                        </span>
                      </td>
                      <td className="py-2 pr-2">
                        {backup.company_name ? (
                          <div className="text-xs text-gray-900 font-bold">
                            {backup.company_name}
                            {backup.site_code ? ` > ${backup.site_code}` : ''}
                            {backup.target_user ? ` > ${backup.target_user}` : ''}
                          </div>
                        ) : (
                          <span className="text-gray-500 italic text-xs">Global</span>
                        )}
                      </td>
                      <td className="py-2 pr-2 text-xs text-gray-600">
                        {backup.modules?.join(', ') || 'N/A'}
                      </td>
                      <td className="py-2 pr-2">
                        <div className="text-xs">
                          <span className="text-gray-800 font-medium">{backup.total_rows}</span> rows
                          <br/>
                          <span className="text-red-500">{backup.total_deleted}</span> deleted
                        </div>
                      </td>
                      <td className="py-2 pr-2 text-xs text-gray-600">
                        {backup.requested_by}
                      </td>
                      <td className="py-2 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-2">
                          <a
                            href={`${(process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api').replace('/api', '')}/media/${backup.backup_file_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-[#0066A3] hover:bg-[#F2F8FC] rounded-lg transition-colors"
                            title="Download ZIP"
                          >
                            <Download size={16} />
                          </a>
                          <button
                            onClick={() => handleDeleteBackup(backup.backup_file_path)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Backup" aria-label="Delete Backup"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataCleanupManagement;
